/**
 * useNotificationStream — SSE GLOBAL permanente.
 *
 * Spec §1, §9, §15.
 *
 * Conecta uma única vez ao endpoint `GET /api/v1/executions/stream?token=<jwt>`,
 * que emite eventos `execution_end` de **todas** as execuções dos tenants
 * acessíveis pelo JWT do usuário (o Orchestrator filtra pelo claim
 * `accessible_tenants`, não pelo `X-Tenant-ID`).
 *
 * Diferenças importantes em relação ao `useSSE` (por execução):
 *
 *   • É instanciado **uma vez** no `AppShell` após login JWT bem-sucedido.
 *   • Não depende do `activeTenant` — sobrevive a trocas de tenant.
 *   • Só opera no modo de autenticação `jwt` — o admin global (HTTP Basic)
 *     não tem JWT, não tem tenant, e não recebe notificações.
 *   • Reconexão é **infinita** (com teto de backoff) — diferente de `useSSE`
 *     que desiste após 3 tentativas. A conexão é permanente: se cair, deve
 *     voltar assim que possível, pois o usuário pode estar com a aba aberta
 *     por horas.
 *   • Filtra apenas eventos `execution_end` — todos os demais tipos (step_start
 *     etc.) chegam pelo stream global mas só são úteis na página de detalhe.
 *
 * Efeitos colaterais ao receber `execution_end`:
 *   1. Adiciona uma `Notification` ao `useNotificationsStore`.
 *   2. Dispara um toast via Sonner com um botão "Ver" que navega para
 *      `/executions/:id`.
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { toast } from '@/components/ui/sonner'
import { config } from '@/lib/config'
import { useAuthStore } from '@/store/auth.store'
import {
  useNotificationsStore,
  type Notification,
} from '@/store/notifications.store'
import type { ExecutionEndEvent, ExecutionEvent } from '@/types/events'

/**
 * Backoff exponencial **com teto** em milissegundos.
 *
 * Diferente do `useSSE` (que tem um número fixo de tentativas), aqui o último
 * valor é repetido indefinidamente após esgotar o array — a conexão deve
 * ser permanente. O teto de 30s evita martelar o servidor quando ele está
 * indisponível por um período prolongado.
 */
const BACKOFF_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000] as const

/** Calcula o delay para uma dada tentativa (0-indexed). Satura no último valor. */
function delayForAttempt(attempt: number): number {
  const idx = Math.min(Math.max(attempt, 0), BACKOFF_DELAYS_MS.length - 1)
  // `as const` da array garante valores literais; o fallback nunca executa
  // (idx está sempre dentro dos bounds), mas o TS exige por causa do
  // noUncheckedIndexedAccess.
  return BACKOFF_DELAYS_MS[idx] ?? BACKOFF_DELAYS_MS[BACKOFF_DELAYS_MS.length - 1] ?? 30_000
}

/**
 * Type guard para eventos `execution_end`. Outros tipos chegam pelo stream
 * mas não viram notificação.
 */
function isExecutionEnd(e: ExecutionEvent): e is ExecutionEndEvent {
  return e.event === 'execution_end'
}

/**
 * Resolve o título exibido na notificação e no toast.
 *
 * O `ExecutionEndEvent` real do Worker carrega `test_id` e (opcionalmente)
 * `execution_id`, mas não traz o `target_name` em texto humano. O snippet
 * ilustrativo da spec usa `event.target_name` como didática; na prática o
 * frontend exibe o `test_id` como rótulo enquanto o usuário pode clicar para
 * navegar e ver os detalhes completos.
 */
function resolveTitle(event: ExecutionEndEvent): string {
  return event.test_id || event.execution_id || 'Execução finalizada'
}

/** Resolve o `execution_id` — o evento garante pelo menos um identificador. */
function resolveExecutionId(event: ExecutionEndEvent): string {
  return event.execution_id ?? event.test_id
}

/** Mapeia o status da execução para o tipo visual do toast. */
function toastForStatus(
  status: ExecutionEndEvent['status'],
  title: string,
  onView: () => void,
): void {
  const action = { label: 'Ver', onClick: onView }
  const opts = { action }

  switch (status) {
    case 'approved':
      toast.success(title, opts)
      return
    case 'rejected':
    case 'error':
      toast.error(title, opts)
      return
    case 'undetermined':
    case 'cancelled':
      toast.warning(title, opts)
      return
    default:
      toast(title, opts)
  }
}

/**
 * Hook sem retorno. Mantém a conexão aberta enquanto o `auth.mode === 'jwt'`.
 * Fecha automaticamente em qualquer mudança de modo (logout → `clearAuth`
 * troca `mode` para `null`) ou no unmount do `AppShell`.
 */
export function useNotificationStream(): void {
  const navigate = useNavigate()

  // `mode` é o gatilho principal: a conexão deve abrir/fechar de acordo com
  // ele. Re-renders causados por outras partes do auth.store (ex: setUser)
  // não devem reabrir a conexão — por isso selecionamos `mode` granularmente.
  const mode = useAuthStore((s) => s.mode)

  // Mantém o callback de navegação atualizado em uma ref para não derrubar a
  // conexão SSE quando o router troca de instância internamente.
  const navigateRef = useRef(navigate)
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  useEffect(() => {
    // Só opera em modo JWT — admin global não tem JWT e o Orchestrator
    // recusaria a conexão de qualquer forma.
    if (mode !== 'jwt') return

    let attempt = 0
    let es: EventSource | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let closedByUser = false

    const buildUrl = (): string => {
      // Pega o token "fresco" a cada conexão: após um refresh do JWT, a
      // reconexão usa o novo token automaticamente.
      const token = useAuthStore.getState().accessToken ?? ''
      const base = config.apiUrl ?? ''
      return `${base}/api/v1/executions/stream?token=${encodeURIComponent(token)}`
    }

    const closeQuietly = (): void => {
      closedByUser = true
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      if (es) {
        es.close()
        es = null
      }
    }

    const handleExecutionEnd = (event: ExecutionEndEvent): void => {
      const executionId = resolveExecutionId(event)
      const title = resolveTitle(event)

      const notification: Notification = {
        // `id` da notificação === `execution_id` (estável, evita duplicatas
        // se o servidor reenviar o evento durante uma reconexão).
        id: executionId,
        type: 'execution_end',
        title,
        status: event.status,
        execution_id: executionId,
        timestamp: event.timestamp,
        read: false,
      }

      useNotificationsStore.getState().add(notification)

      toastForStatus(event.status, title, () => {
        navigateRef.current(`/executions/${executionId}`)
      })
    }

    const handleMessage = (raw: MessageEvent): void => {
      let parsed: ExecutionEvent
      try {
        parsed = JSON.parse(raw.data) as ExecutionEvent
      } catch (err) {
        if (config.isDev) {
          // eslint-disable-next-line no-console
          console.warn('[useNotificationStream] failed to parse event:', err)
        }
        return
      }

      // Recebemos um payload válido → conexão saudável, zera o backoff.
      attempt = 0

      if (isExecutionEnd(parsed)) {
        handleExecutionEnd(parsed)
      }
      // Outros tipos de evento são ignorados pelo stream global.
    }

    const handleError = (): void => {
      if (closedByUser) return

      if (es) {
        es.close()
        es = null
      }

      const delay = delayForAttempt(attempt)
      attempt += 1

      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        if (!closedByUser) connect()
      }, delay)
    }

    const connect = (): void => {
      es = new EventSource(buildUrl())

      // O endpoint global emite o JSON na linha `data:` sem campo `event:`
      // — `onmessage` cobre todos os payloads. Mantemos a paridade com o
      // `useSSE` (que registra listeners nomeados) só onde aplicável.
      es.onmessage = handleMessage
      es.onerror = handleError
    }

    connect()

    return () => {
      closeQuietly()
    }
  }, [mode])
}
